import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, GoneError } from '@teivor/shared-utils/api';

import { Button, FormField, PasswordInput, PasswordStrengthMeter } from '../../lib/ui';

export function PasswordResetConfirmPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [pw, setPw] = React.useState('');
  const [confirm, setConfirm] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (pw.length < 8) return setError(t('auth.password_too_short', 'Password must be at least 8 characters.'));
    if (pw !== confirm) return setError(t('auth.password_mismatch', 'Passwords do not match.'));
    setSubmitting(true);
    try {
      await api.iam.resetConfirm({ token, newPassword: pw });
      navigate('/login?reset=ok');
    } catch (err) {
      if (err instanceof GoneError) {
        setError(t('auth.reset_link_expired', 'This reset link has expired or was already used.'));
      } else {
        setError(t('common.error'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <h1 className="mb-1 text-2xl font-semibold">{t('auth.change_password')}</h1>
      {error ? (
        <div role="alert" className="mb-4 rounded-[var(--radius-md)] border border-[var(--color-danger)] bg-[var(--color-surface-2)] p-3 text-sm text-[var(--color-danger)]">{error}</div>
      ) : null}
      <form onSubmit={submit} className="flex flex-col gap-4">
        <FormField label={t('auth.new_password')} htmlFor="new" required>
          <PasswordInput id="new" value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" />
          <PasswordStrengthMeter value={pw} />
        </FormField>
        <FormField label={t('auth.confirm_password')} htmlFor="confirm" required>
          <PasswordInput id="confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
        </FormField>
        <Button type="submit" fullWidth loading={submitting}>{t('auth.change_password')}</Button>
      </form>
    </div>
  );
}
